/**
 * HelpScene.ts — Multi-page help/guide accessible from title and settings.
 *
 * 4 pages cycled with Next/Previous/Back:
 * 1. Controls — input methods
 * 2. Color Guide — color conventions
 * 3. Game Mechanics — stat checks, leveling, equipment
 * 4. Keyboard Shortcuts — navigation
 *
 * Migrated to FrameRenderer + PanelWriter for framed UI layout.
 */

import type { GameEngine, Scene } from '@core/engine/GameEngine';
import type { MenuOption } from '@ui/MenuSystem';
import type { PanelWriter } from '@ui/PanelWriter';

const PAGE_TITLES = ['Controls', 'Color Guide', 'Game Mechanics', 'Keyboard Shortcuts'];

export class HelpScene implements Scene {
  name = 'HelpScene';

  async enter(engine: GameEngine): Promise<void> {
    let pageIndex = 0;

    while (true) {
      const action = await this.showPage(engine, pageIndex);
      if (action === 'back') {
        await engine.popScene();
        return;
      }
      if (action === 'next') {
        pageIndex = Math.min(pageIndex + 1, PAGE_TITLES.length - 1);
      } else if (action === 'prev') {
        pageIndex = Math.max(pageIndex - 1, 0);
      }
    }
  }

  async exit(engine: GameEngine): Promise<void> {
    engine.getContext().sprites.hide();
  }

  private async showPage(
    engine: GameEngine,
    pageIndex: number,
  ): Promise<'back' | 'next' | 'prev'> {
    const ctx = engine.getContext();
    const { menu, effects, frameRenderer } = ctx;

    const drawPage = () => {
      ctx.sprites.hide();
      const layout = frameRenderer.drawFrame({
        title: 'HELP',
        headerRight: `[${pageIndex + 1}/${PAGE_TITLES.length}] ${PAGE_TITLES[pageIndex]}`,
        borderColor: 'frostBlue',
      });

      const writer = frameRenderer.createWriter(layout.content, effects);
      writer.blankLine();

      switch (pageIndex) {
        case 0: this.renderControlsPage(writer); break;
        case 1: this.renderColorGuidePage(writer); break;
        case 2: this.renderMechanicsPage(writer); break;
        case 3: this.renderShortcutsPage(writer); break;
      }

      return writer;
    };

    const writer = drawPage();

    const options: MenuOption[] = [
      pageIndex < PAGE_TITLES.length - 1
        ? { label: 'Next Page \u2192', color: 'amber' }
        : { label: 'Next Page \u2192', color: 'charcoal', disabled: true },
      pageIndex > 0
        ? { label: '\u2190 Previous Page', color: 'amber' }
        : { label: '\u2190 Previous Page', color: 'charcoal', disabled: true },
    ];

    const menuCfg = writer.menuConfig();
    const sel = await menu.show(options, {
      showExit: true,
      exitLabel: 'Back',
      startRow: menuCfg.startRow,
      startCol: menuCfg.startCol,
      maxWidth: menuCfg.maxWidth,
      onBeforeRender: () => {
        const w = drawPage();
        const mc = w.menuConfig();
        return { startRow: mc.startRow, startCol: mc.startCol, maxWidth: mc.maxWidth };
      },
    });

    if (sel === -1) return 'back';
    const selected = options[sel]?.label ?? '';
    if (selected.includes('Next')) return 'next';
    if (selected.includes('Previous')) return 'prev';
    return 'back';
  }

  private renderControlsPage(writer: PanelWriter): void {
    writer.writeLine('  INPUT METHODS', 'yellow');
    writer.blankLine();
    writer.writeLine('  The game supports three input methods simultaneously:', 'white');
    writer.blankLine();
    writer.writeLine('  1. Number Keys  - Press 1-9 to select menu options', 'frostSilver');
    writer.writeLine('  2. Arrow Keys   - Up/Down to navigate, Enter to select', 'frostSilver');
    writer.writeLine('  3. Mouse Click  - Click on any menu option to select', 'frostSilver');
    writer.blankLine();
    writer.writeLine('  UNIVERSAL CONTROLS', 'yellow');
    writer.blankLine();
    writer.writeLine('  0 / Escape  - Back / Exit current menu', 'white');
    writer.writeLine('  Any key     - Skip typewriter text, dismiss prompts', 'white');
    writer.blankLine();
  }

  private renderColorGuidePage(writer: PanelWriter): void {
    writer.writeLine('  COLOR CONVENTIONS', 'yellow');
    writer.blankLine();
    writer.writeLine('  Yellow    - Headers, section titles, important labels', 'yellow');
    writer.writeLine('  Amber     - Interactive elements, menu options', 'amber');
    writer.writeLine('  Green     - Positive outcomes, healing, success, XP', 'green');
    writer.writeLine('  Red       - Negative outcomes, damage, danger, failures', 'red');
    writer.writeLine('  White     - Narrative text, descriptions, standard content', 'white');
    writer.writeLine('  Silver    - Hints, help text, system info', 'frostSilver');
    writer.writeLine('  Magenta   - Magic, rare items, special events', 'magenta');
    writer.writeLine('  Blue      - NPC names, location names', 'blue');
    writer.writeLine('  Khaki     - Secondary text, descriptions', 'khaki');
    writer.blankLine();
    writer.writeLine('  COLORBLIND MODE', 'yellow');
    writer.blankLine();
    writer.writeLine('  When enabled, symbols are added alongside colors:', 'white');
    writer.writeLine('  [+] Green (positive)   [-] Red (negative)', 'white');
    writer.writeLine('  [!] Yellow (alert)     [*] Magenta (special)', 'white');
    writer.writeLine('  [?] Silver (info)', 'white');
    writer.blankLine();
  }

  private renderMechanicsPage(writer: PanelWriter): void {
    writer.writeLine('  STAT CHECKS', 'yellow');
    writer.blankLine();
    writer.writeLine('  Stat checks are deterministic - no dice rolls.', 'white');
    writer.writeLine('  Your character build determines outcomes.', 'white');
    writer.writeLine('  Some choices require a stat threshold to appear.', 'white');
    writer.blankLine();
    writer.writeLine('  LEVELING', 'yellow');
    writer.blankLine();
    writer.writeLine('  Earn XP through story choices. Level up to gain:', 'white');
    writer.writeLine('  - Increased max HP', 'frostSilver');
    writer.writeLine('  - Stat points to allocate (via Character Sheet)', 'frostSilver');
    writer.blankLine();
    writer.writeLine('  EQUIPMENT & ITEMS', 'yellow');
    writer.blankLine();
    writer.writeLine('  Items are found through story choices. Equipment', 'white');
    writer.writeLine('  provides stat bonuses when equipped.', 'white');
    writer.blankLine();
    writer.writeLine('  FROSTBITE & CORRUPTION', 'yellow');
    writer.blankLine();
    writer.writeLine('  Dark choices may increase Frostbite or Corruption.', 'white');
    writer.writeLine('  These traits affect story outcomes and endings.', 'white');
    writer.blankLine();
  }

  private renderShortcutsPage(writer: PanelWriter): void {
    writer.writeLine('  MENU NAVIGATION', 'yellow');
    writer.blankLine();
    writer.writeLine('  Up / Down Arrow  - Move selection highlight', 'white');
    writer.writeLine('  Enter            - Confirm selection', 'white');
    writer.writeLine('  1-9              - Quick-select option by number', 'white');
    writer.writeLine('  0                - Select Back/Exit option', 'white');
    writer.writeLine('  Escape           - Same as 0 (Back/Exit)', 'white');
    writer.blankLine();
    writer.writeLine('  STORY CONTROLS', 'yellow');
    writer.blankLine();
    writer.writeLine('  Any key          - Skip typewriter animation', 'white');
    writer.writeLine('  Any key          - Dismiss "press any key" prompts', 'white');
    writer.blankLine();
    writer.writeLine('  CHARACTER SHEET', 'yellow');
    writer.blankLine();
    writer.writeLine('  During story gameplay, select "Character Sheet"', 'white');
    writer.writeLine('  (option 0) to view stats, inventory, and save.', 'white');
    writer.blankLine();
  }
}
